import { ChevronDown, Command, Menu, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { navigation } from "@/data/afterflowData";
import { api, clearSession } from "@/lib/api";

export default function AppShell({ children }) {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const location = useLocation();
  const workspaceParts = (currentWorkspace?.name || "Workspace").trim().split(/\s+/);
  const workspaceInitials = (workspaceParts.length > 1
    ? workspaceParts.map((word) => word[0]).join("")
    : workspaceParts[0].slice(0, 2))
    .slice(0, 2)
    .toUpperCase();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    api.get("/auth/me").then((response) => setCurrentWorkspace(response.data.workspace)).catch(() => undefined);
  }, []);

  useEffect(() => {
    const shortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, []);

  useEffect(() => {
    if (search.length < 2) return setResults([]);
    const timeout = setTimeout(() => api.get(`/search?query=${encodeURIComponent(search)}`)
      .then((response) => setResults(response.data))
      .catch(() => setResults([])), 180);
    return () => clearTimeout(timeout);
  }, [search]);

  const openStores = () => {
    api.get("/workspaces").then((response) => setWorkspaces(response.data)).catch(() => setWorkspaces([]));
  };

  return (
    <div className="app-frame" data-testid="app-shell">
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`} data-testid="sidebar-navigation">
        <div className="sidebar-top">
          <Link className="brand" to="/app" data-testid="deewel-home-link">
            <img className="brand-horizontal-logo" src="/brand/deewel-primary.png" alt="DEEWEL" />
          </Link>
          <button className="icon-button mobile-only" onClick={() => setOpen(false)}
            data-testid="close-navigation-button" aria-label="Close navigation">
            <X size={18} />
          </button>
        </div>
        <button className="store-switcher" onClick={openStores} data-testid="store-selector-button">
          <span className="store-bag">S</span>
          <span className="store-copy"><strong>{currentWorkspace?.shopify_shop_name || "Shopify authorization needs attention"}</strong><small>{currentWorkspace?.shop_domain || "Connect a Shopify store"}</small></span>
          <ChevronDown size={15} />
        </button>
        {workspaces.length > 0 && <div className="store-menu" data-testid="store-selector-menu">{workspaces.map((workspace) => <button key={workspace.workspace_id} onClick={() => api.post(`/workspaces/${workspace.workspace_id}/switch`).then(() => window.location.reload())} data-testid={`switch-store-${workspace.workspace_id}`}>{workspace.store_name}</button>)}</div>}
        <nav className="side-nav" aria-label="Primary navigation">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`nav-item ${active ? "nav-item-active" : ""}`}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
              >
                <Icon size={16} strokeWidth={1.8} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-profile" data-testid="workspace-profile">
          <span className="profile-avatar">{workspaceInitials}</span>
          <span><strong>{currentWorkspace?.shopify_shop_name || "Shopify authorization needs attention"}</strong><small>{currentWorkspace?.shop_domain || "Owner workspace"}</small></span>
          <ChevronDown size={14} />
        </div>
      </aside>
      <main className="main-area">
        <header className="topbar">
          <button className="icon-button mobile-only" onClick={() => setOpen(true)}
            data-testid="open-navigation-button" aria-label="Open navigation">
            <Menu size={19} />
          </button>
          <button className="command-search" onClick={() => setSearchOpen(true)} data-testid="global-search-button">
            <Search size={16} />
            <span>Search anything</span>
            <kbd><Command size={11} />K</kbd>
          </button>
          <div className="topbar-right">
            <span className="live-status" data-testid="live-status"><i />All systems live</span>
            <button className="avatar-button" onClick={() => api.post("/auth/logout").finally(() => { clearSession(); window.location.assign("/login"); })} data-testid="account-menu-button" aria-label="Sign out">{workspaceInitials}</button>
          </div>
        </header>
        {children}
      </main>
      {searchOpen && <div className="command-dialog" data-testid="global-search-dialog"><div><input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Escape") setSearchOpen(false); if (event.key === "Enter" && results[0]) window.location.assign("/app/requests"); }} placeholder="Search customers, orders, products, and requests" data-testid="global-search-input" />{results.map((result) => <button key={result.id} onClick={() => { setSearchOpen(false); window.location.assign("/app/requests"); }} data-testid={`global-search-result-${result.id}`}><strong>{result.title}</strong><small>{result.kind} · {result.subtitle}</small></button>)}{search.length > 1 && !results.length && <p data-testid="global-search-empty">No matching records</p>}</div></div>}
    </div>
  );
}